import {
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item,
  isNoProtectionInsuranceItem,
  isInsuranceItem,
  isBedbankItem,
  isExperienceTicketViewItem,
  isExperienceItem,
  isAccommodationV2Item,
  isLEHotelItem,
  isLoyaltyTransferItem,
  isLoyaltyLoungePassItem,
  isFlightItem,
  getAccommodationV2ItemService,
} from 'lib/checkout/checkoutUtils'
import { calculateTotalJourneyBaggageCost } from 'checkout/lib/utils/flights/extras'
import { computeFeesTotal } from 'checkout/lib/utils/flights/fees'
import { sum } from 'lib/array/arrayUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { getAgentHubItemTotalCommissionAmount } from 'lib/payment/agentHubUtils'

export function mapItemType(
  item: App.Checkout.AnyItem,
  accommodationSessions: App.CheckoutState['accommodationSessions'] = {},
): string {
  if (isTourV2Item(item)) {
    return 'tour_v2'
  }
  if (isTourV2ExperienceItem(item)) {
    return 'tour_optional_experience'
  }
  if (isBedbankItem(item)) {
    return 'bedbank'
  }
  // v2 can be either supplier; after fallback_selected the session selectedSource wins.
  if (isAccommodationV2Item(item)) {
    return getAccommodationV2ItemService(item, accommodationSessions) === 'bedbank' ? 'bedbank' : 'accommodation'
  }
  if (isLEHotelItem(item)) {
    return 'accommodation'
  }
  if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
    return 'subscription'
  }
  if (isExperienceTicketViewItem(item) || isLoyaltyLoungePassItem(item)) {
    return 'experience'
  }
  if (isLoyaltyTransferItem(item)) {
    return 'transfer'
  }
  if (isFlightItem(item)) {
    return 'flight'
  }
  return item.itemType
}

export function mapOfferType(item: App.Checkout.AnyItem, offers: Record<string, App.AnyOffer>): string | undefined {
  if (!isLEHotelItem(item)) {
    return undefined
  }
  const offer = offers[item.offerId]
  return offer?.type
}

export function calculateGiftCardAmount(redeemedGiftCards: Array<{ amount: number }>): number {
  return sum(redeemedGiftCards.map(giftCard => giftCard.amount))
}

export interface PaymentScheduleItemContext {
  offers: Record<string, App.AnyOffer>
  discountTotalToCartItemsMap: Map<string, number | undefined>
  voucherTotalToCartItemsMap?: Map<string, number | undefined>
  checkoutWithMemberPrice: boolean
  insuranceTotals: App.WithDataStatus<App.Checkout.ItemViewTotals>
  accommodationBreakdownView: App.WithDataStatus<{ tours: Array<App.Checkout.PriceBreakdownView>; accommodations: Array<App.Checkout.PriceBreakdownView> }>
  itemViews?: App.WithDataStatus<Map<string, App.WithDataStatus<App.Checkout.AnyItemView>>>
  experienceItemViews?: App.WithDataStatus<Array<App.Checkout.ExperienceItemView>>
  loyaltyLoungePassItemViews?: App.WithDataStatus<Array<App.Checkout.LoyaltyLoungePassItemView>>
  loyaltyTransferItemViews?: App.WithDataStatus<Array<App.Checkout.LoyaltyTransferItemView>>
  agentHubCommission?: {
    shouldUse: boolean
    itemsCommission: Record<string, Pick<App.AgentHubItemCommissionDetails, 'itemDiscount' | 'totalIncentiveAmount'>> | undefined
  }
  accommodationSessions?: App.CheckoutState['accommodationSessions']
}

export function buildPaymentScheduleItems(
  cartItems: Array<App.Checkout.AnyItem>,
  ctx: PaymentScheduleItemContext,
) {
  const items = cartItems
    .filter(item => !(isSubscriptionJoinItem(item) && item.waived))
    .filter(item => !isNoProtectionInsuranceItem(item))
    .map((item) => {
      let total: number | undefined = undefined
      let discountTotal = ctx.discountTotalToCartItemsMap.has(item.itemId) ? ctx.discountTotalToCartItemsMap.get(item.itemId) : 0
      let voucherForItem = ctx.voucherTotalToCartItemsMap?.get(item.itemId) ?? 0

      if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
        total = item.amount ?? undefined
      } else if (isInsuranceItem(item)) {
        total = ctx.insuranceTotals.hasRequiredData ? ctx.insuranceTotals.data.price : 0
      } else if (isBedbankItem(item) && ctx.itemViews) {
        const bedbankItemView = ctx.itemViews.data.get(item.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
        total = (ctx.checkoutWithMemberPrice && bedbankItemView?.totals?.memberPrice) ? bedbankItemView?.totals?.memberPrice : bedbankItemView?.totals?.price ?? undefined
      } else if (isAccommodationV2Item(item) && ctx.itemViews) {
        // Session sellTotal lives on the item view as `totals.price`. Do not use the LE
        // hotel path (`price + taxesAndFees`) — sellTotal is already the payable-now
        // amount. Property fees are due at the property and live on `otherFees`.
        const itemView = ctx.itemViews.data.get(item.itemId)?.data as App.Checkout.AccommodationV2AccommodationItemView | undefined
        total = itemView?.totals?.price ?? undefined
      } else if (isExperienceItem(item) && ctx.experienceItemViews) {
        const itemView = ctx.experienceItemViews.data.find(view => view.id === item.itemId)
        total = (ctx.checkoutWithMemberPrice && itemView?.totals?.memberPrice) ? itemView.totals.memberPrice : itemView?.totals?.price ?? undefined
        const discountTotals = itemView?.ticketViews?.map((ticketView) => ctx.discountTotalToCartItemsMap.get(ticketView.item.itemId)) ?? []
        if (discountTotals.every(dt => dt !== undefined)) {
          discountTotal = sum(discountTotals)
        }
        const voucherTotals = itemView?.ticketViews?.map((ticketView) => ctx.voucherTotalToCartItemsMap?.get(ticketView.item.itemId)) ?? []
        voucherForItem = sum(voucherTotals.map(v => v ?? 0))
      } else if (isLEHotelItem(item)) {
        const hotelBreakdownView = ctx.accommodationBreakdownView.data.accommodations.find(view => view.items.some(itemView => itemView.itemId === item.itemId))
        const hotelItemView = hotelBreakdownView?.items.find(itemView => itemView.itemId === item.itemId)
        total = (ctx.checkoutWithMemberPrice && hotelItemView?.memberPrice) ? hotelItemView?.memberPrice : hotelItemView?.price ?? undefined
        total = total ? (total + (hotelItemView?.taxesAndFees ?? 0)) : undefined
      } else if (isLoyaltyTransferItem(item) && ctx.loyaltyTransferItemViews) {
        const transferItemView = ctx.loyaltyTransferItemViews.data.find(view => view.item.itemId === item.itemId)
        total = transferItemView?.totals?.price ?? 0
      } else if (isLoyaltyLoungePassItem(item) && ctx.loyaltyLoungePassItemViews) {
        const loungePassItemView = ctx.loyaltyLoungePassItemViews.data.find(view => view.item.itemId === item.itemId)
        total = loungePassItemView?.totals?.price ?? 0
      } else if (isFlightItem(item) && ctx.itemViews) {
        const flightItemView = ctx.itemViews.data.get(item.itemId)?.data as App.Checkout.FlightItemView | undefined
        const baggageTotal = flightItemView ? calculateTotalJourneyBaggageCost(flightItemView) : 0
        const flightFees = (item.otherFees?.flightSelectedSeatsTotal ?? 0) + baggageTotal + computeFeesTotal(item.otherFees ?? {})
        total = sum(
          item.flights.map(flight => flight.cost ?? 0),
        ) + flightFees
      } else {
        total = (ctx.checkoutWithMemberPrice && item.memberTotal) ? item.memberTotal : item.total
      }

      const commission = ctx.agentHubCommission?.shouldUse
        ? getAgentHubItemTotalCommissionAmount(
            ctx.agentHubCommission.itemsCommission?.[getAgentHubCommissionItemKey(item)],
          )
        : 0
      const isPaymentScheduleSupported = isFlightItem(item) && item.flights.some(flight => flight.paymentScheduleSupported === true)

      const agentHubDiscountTotal = commission ? commission + voucherForItem : discountTotal

      return {
        idItems: item.itemId,
        total,
        type: mapItemType(item, ctx.accommodationSessions),
        discountTotal: ctx.agentHubCommission?.shouldUse ? agentHubDiscountTotal : discountTotal,
        discountBreakdown: { voucherAmount: voucherForItem },
        offerType: mapOfferType(item, ctx.offers),
        ...(isPaymentScheduleSupported && { isPaymentScheduleSupported: true }),
      }
    })

  if (!items.every((item): item is typeof item & { total: number; discountTotal: number } => item.total !== undefined && item.discountTotal !== undefined)) {
    return null
  }

  return items
}
